/**
 * Simple markdown to HTML converter for lesson content
 * Converts basic markdown syntax to HTML that TipTap can understand
 */
export function markdownToHtml(markdown) {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  // If it's already HTML (contains HTML tags), return as-is
  // Check for common HTML tags to determine if it's already HTML
  // Look for opening tags with proper structure
  const htmlTagPattern = /<\/?(p|div|h[1-6]|ul|ol|li|strong|em|b|i|u|a|img|br|hr|blockquote|pre|code|span|table|tr|td|th|thead|tbody)[\s>\/]/i;
  if (htmlTagPattern.test(markdown)) {
    // Double-check: if it has HTML structure, return as-is
    // Count HTML tags vs markdown syntax
    const htmlTagCount = (markdown.match(/<\/?[a-z][\s>]/gi) || []).length;
    const markdownHeaderCount = (markdown.match(/^#{1,6}\s/gm) || []).length;
    
    // If there are more HTML tags than markdown headers, it's likely HTML
    if (htmlTagCount > markdownHeaderCount) {
      return markdown;
    }
  }

  let html = markdown;

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');

  // Process lists more carefully - need to handle them line by line
  // Process lists before other block elements
  const listLines = html.split('\n');
  const listProcessedLines = [];
  let inUnorderedList = false;
  let inOrderedList = false;
  
  for (let i = 0; i < listLines.length; i++) {
    const line = listLines[i];
    const trimmedLine = line.trim();
    
    // Check for unordered list item (must be at start of line after trimming)
    if (/^[-*]\s/.test(trimmedLine)) {
      if (!inUnorderedList) {
        if (inOrderedList) {
          listProcessedLines.push('</ol>');
          inOrderedList = false;
        }
        listProcessedLines.push('<ul>');
        inUnorderedList = true;
      }
      const content = trimmedLine.replace(/^[-*]\s+/, '');
      listProcessedLines.push(`<li>${content}</li>`);
    }
    // Check for ordered list item
    else if (/^\d+\.\s/.test(trimmedLine)) {
      if (!inOrderedList) {
        if (inUnorderedList) {
          listProcessedLines.push('</ul>');
          inUnorderedList = false;
        }
        listProcessedLines.push('<ol>');
        inOrderedList = true;
      }
      const content = trimmedLine.replace(/^\d+\.\s+/, '');
      listProcessedLines.push(`<li>${content}</li>`);
    }
    // Not a list item
    else {
      if (inUnorderedList) {
        listProcessedLines.push('</ul>');
        inUnorderedList = false;
      }
      if (inOrderedList) {
        listProcessedLines.push('</ol>');
        inOrderedList = false;
      }
      listProcessedLines.push(line);
    }
  }
  
  // Close any open lists
  if (inUnorderedList) {
    listProcessedLines.push('</ul>');
  }
  if (inOrderedList) {
    listProcessedLines.push('</ol>');
  }
  
  html = listProcessedLines.join('\n');

  // Code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Blockquotes
  html = html.replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Horizontal rules
  html = html.replace(/^---$/gim, '<hr>');
  html = html.replace(/^\*\*\*$/gim, '<hr>');

  // Markdown tables
  // Match table rows (lines starting with |)
  const tableRegex = /^\|(.+)\|$/gm;
  const tableSeparatorRegex = /^\|[\s\-\|:]+\|$/gm;
  
  // Process tables before paragraph processing
  const tableLines = html.split('\n');
  const tableProcessedLines = [];
  let i = 0;
  let inTable = false;
  let tableRows = [];
  let isHeaderRow = false;
  
  while (i < tableLines.length) {
    const line = tableLines[i].trim();
    const isTableRow = line.match(/^\|.+\|$/);
    const isTableSeparator = line.match(/^\|[\s\-\|:]+\|$/);
    
    if (isTableRow && !isTableSeparator) {
      // Start or continue table
      if (!inTable) {
        inTable = true;
        tableRows = [];
        isHeaderRow = true;
      }
      
      // Parse table cells
      const cells = line.split('|').slice(1, -1).map(cell => cell.trim());
      tableRows.push({ cells, isHeader: isHeaderRow });
      isHeaderRow = false;
      i++;
    } else if (isTableSeparator && inTable) {
      // Skip separator row
      i++;
    } else {
      // End of table or not a table
      if (inTable && tableRows.length > 0) {
        // Convert table rows to HTML
        let tableHtml = '<table><thead><tr>';
        const headerRow = tableRows.find(r => r.isHeader) || tableRows[0];
        headerRow.cells.forEach(cell => {
          tableHtml += `<th>${cell || ''}</th>`;
        });
        tableHtml += '</tr></thead><tbody>';
        
        tableRows.forEach((row, idx) => {
          if (!row.isHeader) {
            tableHtml += '<tr>';
            row.cells.forEach(cell => {
              tableHtml += `<td>${cell || ''}</td>`;
            });
            tableHtml += '</tr>';
          }
        });
        
        tableHtml += '</tbody></table>';
        tableProcessedLines.push(tableHtml);
        inTable = false;
        tableRows = [];
      }
      
      // Add current line
      tableProcessedLines.push(tableLines[i]);
      i++;
    }
  }
  
  // Handle table at end of content
  if (inTable && tableRows.length > 0) {
    let tableHtml = '<table><thead><tr>';
    const headerRow = tableRows.find(r => r.isHeader) || tableRows[0];
    headerRow.cells.forEach(cell => {
      tableHtml += `<th>${cell || ''}</th>`;
    });
    tableHtml += '</tr></thead><tbody>';
    
    tableRows.forEach((row) => {
      if (!row.isHeader) {
        tableHtml += '<tr>';
        row.cells.forEach(cell => {
          tableHtml += `<td>${cell || ''}</td>`;
        });
        tableHtml += '</tr>';
      }
    });
    
    tableHtml += '</tbody></table>';
    tableProcessedLines.push(tableHtml);
  }
  
  html = tableProcessedLines.join('\n');

  // Paragraphs - wrap consecutive non-block elements in <p> tags
  const linesForParagraphs = html.split('\n');
  const processedLines = [];
  let currentParagraph = [];
  let inList = false;

  for (let i = 0; i < linesForParagraphs.length; i++) {
    const line = linesForParagraphs[i].trim();
    
    // Skip empty lines
    if (!line) {
      if (currentParagraph.length > 0) {
        processedLines.push('<p>' + currentParagraph.join(' ') + '</p>');
        currentParagraph = [];
      }
      inList = false;
      continue;
    }

    // If it's a block element, close current paragraph and add the block
    if (line.match(/^<(h[1-6]|ul|ol|blockquote|pre|hr|table)/)) {
      if (currentParagraph.length > 0) {
        processedLines.push('<p>' + currentParagraph.join(' ') + '</p>');
        currentParagraph = [];
      }
      processedLines.push(line);
      if (line.match(/^<(ul|ol)/)) {
        inList = true;
      } else {
        inList = false;
      }
    } else if (line.match(/^<\/?(ul|ol|h[1-6]|blockquote|pre|hr|table|thead|tbody|tr|td|th)/)) {
      // List item or closing tag
      processedLines.push(line);
      if (line.match(/^<\/(ul|ol)>/)) {
        inList = false;
      }
    } else if (inList && line.match(/^<li>/)) {
      // List item when in a list
      processedLines.push(line);
    } else {
      // Regular content - add to current paragraph
      if (inList) {
        // If we're in a list but this isn't a list item, close the list
        inList = false;
      }
      currentParagraph.push(line);
    }
  }

  // Close any remaining paragraph
  if (currentParagraph.length > 0) {
    processedLines.push('<p>' + currentParagraph.join(' ') + '</p>');
  }

  html = processedLines.join('\n');

  // Clean up nested lists (fix any issues)
  html = html.replace(/<\/ul>\s*<ul>/g, '');
  html = html.replace(/<\/ol>\s*<ol>/g, '');
  html = html.replace(/<p>\s*<\/p>/g, ''); // Remove empty paragraphs

  return html;
}
